import { useTranslation } from 'react-i18next'

import { formatLongDate } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import type { NumberHealthRead } from '#/shared/api/generated/communication/models'

import { getTooltipModel } from '../lib/derive'
import type { HealthKind, HealthTone } from '../lib/derive'

const TONE_CLASS: Record<HealthTone, string> = {
  ok: 'text-green-700',
  warn: 'text-orange-700',
  bad: 'text-red-500',
  neutral: 'text-gray-900',
  muted: 'text-warm-500',
}

/** A "Label ······ value" line; the value carries its own severity colour. */
function Row({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string
  tone?: HealthTone
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-warm-500">{label}</span>
      <span className={cn('font-medium', TONE_CLASS[tone])}>{value}</span>
    </div>
  )
}

/**
 * The status line atop the hover card: the headline plus a one-line takeaway of
 * what the agent should do. Shared with the badge's lite tooltip so an
 * unknown-customer badge reads with the same voice.
 */
export function NumberHealthTooltipHeader({
  titleKey,
  tone,
  summary,
}: {
  titleKey: string
  tone: HealthTone
  summary: string
}) {
  const { t } = useTranslation()
  return (
    <div className="min-w-0">
      <p className={cn('text-sm leading-snug font-semibold', TONE_CLASS[tone])}>
        {t(titleKey)}
      </p>
      <p className="mt-0.5 leading-snug text-warm-500">{summary}</p>
    </div>
  )
}

/** The opt-out date rides on the summary line when present. */
function summaryFor(
  t: ReturnType<typeof useTranslation>['t'],
  kind: HealthKind,
  optedOutIso: string | null,
): string {
  if (kind === 'optedOut' && optedOutIso) {
    return t('numberHealth.tooltip.summary.optedOutOn', {
      date: formatLongDate(optedOutIso),
    })
  }
  return t(`numberHealth.tooltip.summary.${kind}`)
}

/**
 * The hover card behind a Number Health badge: a status header with a
 * plain-language takeaway, then the per-channel deliverability, line type +
 * carrier, and when the number was last verified. The badge only renders for
 * blocked/invalid numbers, but the card handles every status so it can also
 * back a healthy number elsewhere.
 */
export function NumberHealthTooltip({ health }: { health: NumberHealthRead }) {
  const { t } = useTranslation()
  const model = getTooltipModel(health)

  const lineType = model.carrier
    ? t('numberHealth.lineType.withCarrier', {
        type: t(model.lineTypeKey),
        carrier: model.carrier,
      })
    : t(model.lineTypeKey)

  const sms = model.sms.iso
    ? t('numberHealth.value.optedOutOn', {
        date: formatLongDate(model.sms.iso),
      })
    : t(model.sms.key)

  return (
    <div className="flex w-64 flex-col gap-2.5 text-xs">
      <NumberHealthTooltipHeader
        titleKey={model.title.key}
        tone={model.title.tone}
        summary={summaryFor(t, model.title.kind, model.sms.iso)}
      />

      <div className="flex flex-col gap-2 border-t border-warm-100 pt-2.5">
        <Row label={t('numberHealth.tooltip.lineType')} value={lineType} />
        <Row
          label={t('numberHealth.tooltip.voice')}
          value={t(model.voice.key)}
          tone={model.voice.tone}
        />
        <Row
          label={t('numberHealth.tooltip.sms')}
          value={sms}
          tone={model.sms.tone}
        />
        <Row
          label={t(model.verified.labelKey)}
          value={model.verified.iso ? formatLongDate(model.verified.iso) : '—'}
          tone="muted"
        />
      </div>
    </div>
  )
}
